/** The per-location POS credential that authenticates this till (DEV-412 / DEV-378).
 *
 * V1 identified an install by `webext_uuid` — a bare identifier that proved nothing,
 * since anyone who learned it could impersonate the till. V2 replaces it with a real
 * credential: a username and password generated when the location is linked, shown once,
 * presented as HTTP Basic, and rotatable without touching anything else.
 *
 * `webext_uuid` is therefore no longer required to boot, and requiring it was blocking
 * the app on a value nothing uses any more.
 *
 * THIS IS THE FALLBACK PATH, not the normal one. Measured on real hardware 2026-08-20:
 * K-Series authenticates the extension's PAGE LOAD with HTTP Basic and passes nothing on
 * the URL, so on the deployed same-origin setup the web view replays those credentials
 * itself and this module returns null. An earlier version of this comment said the
 * credential arrives on the URL; it does not, and that guess is recorded here so it is
 * not made twice.
 *
 * What remains is local development, where the surface is not same-origin and there is no
 * web view to replay anything, so a credential can be supplied on the URL by hand.
 *
 * NOTHING IS PERSISTED. An earlier version kept the URL credential in `localStorage` so a
 * reload would not lose it, which put a password somewhere any injected script can read
 * it. It bought nothing: the till never takes this path, and a developer who put the
 * credential in the URL still has it in the URL after a reload. The secret now lives
 * exactly as long as the page does.
 */

export interface PosCredential {
  username: string
  password: string
}

/** Accepted spellings on the configured URL. `u`/`p` keep the URL short enough to type
 * by hand without mistakes. */
const USERNAME_PARAMS = ['pos_user', 'username', 'u']
const PASSWORD_PARAMS = ['pos_password', 'password', 'p']

function firstParam(params: URLSearchParams, names: string[]): string | null {
  for (const name of names) {
    const value = params.get(name)
    if (value && value.trim()) return value.trim()
  }
  return null
}

/** The credential supplied on the URL, if there is one.
 *
 * Null is the expected answer on a real till, and it is not an error: `callPos` sends
 * `credentials: 'include'` so the web view attaches the Basic credential K-Series already
 * authenticated the page with. This exists for the case where no such replay happens.
 */
export function readPosCredential(): PosCredential | null {
  try {
    const params = new URLSearchParams(window.location.search)
    const username = firstParam(params, USERNAME_PARAMS)
    const password = firstParam(params, PASSWORD_PARAMS)
    if (username && password) return { username, password }
  } catch {
    // No URL to read (tests, SSR). No credential, which is the normal answer anyway.
  }
  return null
}

/** The `Authorization` header value for a credential. */
export function basicAuthHeader(credential: PosCredential): string {
  return `Basic ${btoa(`${credential.username}:${credential.password}`)}`
}

/** Never log or display the password. The username alone identifies which credential a
 * till is using, which is all anyone debugging needs. */
export function describeCredential(credential: PosCredential | null): string {
  return credential ? `${credential.username} (password set)` : '(none)'
}
